import * as React from 'react';
import { useMemo } from 'react';
import { createViewConfig } from '@sail/engine';
import { view, css } from '@sail/ui';
import { helmTokens } from '../../theme';
export const StripeConfig = createViewConfig({
    props: {},
    name: 'Stripe',
    defaults: {
        inset: 'normal',
        width: 'normal',
        intersectionInset: 'normal',
        intersectionWidth: 'normal',
        position: 'top-left'
    }
});
const colorMap = {
    first: helmTokens.color.blendBackground,
    second: helmTokens.color.blendForeground
};
const colorAccentMap = {
    primary: helmTokens.color.blendForeground,
    secondary: helmTokens.color.blendBackground,
    light: helmTokens.color.stripeAccentLight,
    white: helmTokens.color.stripeAccentWhite,
    dark: helmTokens.color.stripeAccentDark
};
const intersectionInsetMap = {
    small: `calc(var(--stripeMaxInset) * 0.25)`,
    normal: `calc(var(--stripeMaxInset) * 0.5)`,
    medium: `calc(var(--stripeMaxInset) * 0.75)`,
    large: 'var(--stripeMaxInset)'
};
const insetMap = {
    small: `calc(var(--stripeMaxInset) * 0.125)`,
    normal: `calc(var(--stripeMaxInset) * 0.25)`,
    medium: `calc(var(--stripeMaxInset) * 0.5)`,
    large: `calc(var(--stripeMaxInset) * 0.75)`,
    half: 'var(--stripeMaxInset)',
    // keep this one the same, but we have to return something
    full: 'var(--stripeMaxInset)'
};
export const Stripe = StripeConfig.createView(({ color, position, accent, order, width, inset, outline, intersectionInset, intersectionWidth, intersectionRef, subviews, type, ...props })=>{
    const offsetBorder = position === 'top-left' || position === 'top-right' || position === 'bottom-left';
    const stripeColor = useMemo(()=>{
        if (color) return color;
        if (accent) return colorAccentMap[accent];
        if (order === 'last') return 'transparent';
        return colorMap[order];
    }, [
        color,
        accent,
        order
    ]);
    const outlineShift = order === 'second' && outline ? css({
        top: `calc(var(--stripeIntersectionTop) - 1px)`,
        zIndex: '-1'
    }) : css({});
    return /*#__PURE__*/ React.createElement(view.div, {
        "aria-hidden": "true",
        uses: [
            css({
                '--stripeBorderOffset': offsetBorder ? '-1px' : '0px',
                '--stripeColor': stripeColor
            }),
            outlineShift
        ],
        ...props
    }, type === 'intersection' ? /*#__PURE__*/ React.createElement(view.div, {
        inherits: subviews.intersection,
        ref: intersectionRef
    }) : null);
}, {
    forward: {
        position: true,
        order: true,
        accent: true,
        outline: true
    },
    css: {
        right: 'var(--stripeStart)',
        width: `calc(var(--stripeWidth) + var(--stripeInset) - var(--stripeBorderOffset) * 2)`,
        position: 'absolute',
        height: 'var(--stripeHeight)',
        backgroundColor: 'var(--stripeColor)',
        overflow: 'hidden'
    },
    uses: [
        css({
            '--stripeInset': '0px',
            '--stripeWidth': 'var(--gutterWidth)',
            '--stripeMinWidth': '80px',
            '--stripeHeight': '40px',
            '--stripeStartMax': `calc(var(--layoutWidth) + var(--gutterWidth))`,
            '--stripeBorderOffset': '0px',
            '--stripeIntersectionInset': '0px',
            '--stripeIntersectionTop': `calc(var(--stripeSetHeight) - var(--stripeHeight))`,
            '--stripeIntersectionWidth': 'var(--gutterWidth)',
            '--stripeIntersectionBackground': helmTokens.color.blendIntersection,
            '--stripeMaxInset': 'calc(var(--layoutWidth) / 2)',
            '--stripeStart': 'calc(var(--stripeStartMax) - var(--stripeInset) + var(--stripeBorderOffset))'
        })
    ],
    variants: {
        intersectionInset: {
            small: {
                css: {
                    '--stripeIntersectionInset': intersectionInsetMap.small
                }
            },
            medium: {
                css: {
                    '--stripeIntersectionInset': intersectionInsetMap.medium
                }
            },
            normal: {
                css: {
                    '--stripeIntersectionInset': intersectionInsetMap.normal
                }
            },
            large: {
                css: {
                    '--stripeIntersectionInset': intersectionInsetMap.large
                }
            }
        },
        width: {
            none: {
                css: {
                    '--stripeWidth': '0px'
                }
            },
            small: {
                css: {
                    '--stripeWidth': '80px'
                }
            },
            medium: {
                css: {
                    '--stripeWidth': '240px'
                }
            },
            normal: {
                css: {
                    '--stripeWidth': '160px'
                }
            },
            large: {
                css: {
                    '--stripeWidth': '320px'
                }
            }
        },
        intersectionWidth: {
            none: {
                css: {
                    '--stripeIntersectionWidth': '0px'
                }
            },
            small: {
                css: {
                    '--stripeIntersectionWidth': '80px'
                }
            },
            medium: {
                css: {
                    '--stripeIntersectionWidth': '240px'
                }
            },
            normal: {
                css: {
                    '--stripeIntersectionWidth': '160px'
                }
            },
            large: {
                css: {
                    '--stripeIntersectionWidth': '320px'
                }
            }
        },
        inset: {
            small: {
                css: {
                    '--stripeInset': insetMap.small
                }
            },
            medium: {
                css: {
                    '--stripeInset': insetMap.medium
                }
            },
            normal: {
                css: {
                    '--stripeInset': insetMap.normal
                }
            },
            large: {
                css: {
                    '--stripeInset': insetMap.large
                }
            },
            half: {
                css: {
                    '--stripeInset': insetMap.half
                }
            },
            full: {
                css: {
                    left: 'var(--gutterWidth)',
                    right: 'auto',
                    width: 'var(--stripeWidth)'
                }
            }
        },
        accent: {
            dark: {
                css: {
                    '--stripeIntersectionBackground': '#0055b3'
                }
            }
        },
        order: {
            second: {
                css: {
                    top: 'var(--stripeIntersectionTop)'
                }
            }
        },
        outline: {
            true: {
                css: {
                    backgroundColor: 'none',
                    border: 'solid 1px var(--stripeColor)'
                }
            }
        },
        position: {
            'top-right': {
                css: {
                    right: 'auto',
                    left: 'var(--stripeStart)'
                },
                subviews: {
                    intersection: {
                        css: {
                            left: 'auto',
                            right: 'calc(var(--stripeWidth) - var(--stripeIntersectionWidth))'
                        }
                    }
                }
            },
            'bottom-right': {
                css: {
                    right: 'auto',
                    left: 'var(--stripeStart)',
                    ':only-child': {
                        top: 'var(--stripeIntersectionTop)'
                    }
                },
                subviews: {
                    intersection: {
                        css: {
                            left: 'auto',
                            right: 'calc(var(--stripeWidth) - var(--stripeIntersectionWidth))'
                        }
                    }
                }
            },
            'bottom-left': {
                css: {
                    ':only-child': {
                        top: 'var(--stripeIntersectionTop)'
                    }
                }
            }
        }
    },
    subviews: {
        intersection: {
            css: {
                position: 'absolute',
                top: 'var(--stripeIntersectionTop)',
                height: 'var(--stripeHeight)',
                transform: 'translateY(-0.25px)' /* subpixel shift necessary to fix jagged edge */ ,
                backgroundColor: 'var(--stripeIntersectionBackground)',
                left: 'calc(var(--stripeWidth) - var(--stripeIntersectionWidth))',
                width: `calc(var(--stripeIntersectionWidth) + var(--stripeIntersectionInset))`
            }
        }
    }
});

//# sourceMappingURL=Stripe.js.map